import { FormEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Section from 'app/frontend/base/components/Section'
import Card from 'app/frontend/base/components/Card'
import RealmSelect from 'app/frontend/base/components/RealmSelect'
import CharacterClassSelect from 'app/frontend/base/components/CharacterClassSelect'
import ManualSyncNotice from 'app/frontend/base/components/ManualSyncNotice'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useClaimManualCharacter } from 'app/frontend/base/config/mutations'
import CharacterClass from 'app/frontend/types/custom/CharacterClass'
import CharacterTable from '../UserCharacters/components/CharacterTable'

const ManualCharacters = () => {
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()
  const [selectedRealm, setSelectedRealm] = useState<wowaudit.Realm>()
  const [characterName, setCharacterName] = useState('')
  const [surname, setSurname] = useState('')
  const [characterClass, setCharacterClass] = useState<CharacterClass>()
  const claimMutation = useClaimManualCharacter()

  const characters = useMemo(
    () =>
      (user?.characterReferences ?? []).filter((character) =>
        character.profileId.startsWith('manual-'),
      ),
    [user?.characterReferences],
  )

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!characterClass || !selectedRealm) return

    claimMutation.mutate(
      {
        realmId: selectedRealm.id,
        name: characterName,
        surname,
        characterClass,
      },
      {
        onSuccess: () => {
          setCharacterName('')
          setSurname('')
          setCharacterClass(undefined)
        },
      },
    )
  }

  if (!user) return null

  return (
    <Section title='Profile'>
      <h2>{t('profile.header.characters')}</h2>

      <ManualSyncNotice context='profile' className='mb-4' />

      <Card className='mb-6'>
        <form onSubmit={submit} className='flex flex-col gap-3'>
          <div>
            <h3 className='text-base font-medium'>
              {t('profile.manual-characters.add-title')}
            </h3>
            <p className='text-sm text-muted-foreground'>
              {t('profile.manual-characters.add-description')}
            </p>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <RealmSelect realm={selectedRealm} setRealm={setSelectedRealm} />
            <Input
              value={characterName}
              onChange={(event) => setCharacterName(event.target.value)}
              placeholder={t('profile.manual-characters.name')}
              maxLength={12}
              className='w-40'
            />
            <Input
              value={surname}
              onChange={(event) => setSurname(event.target.value)}
              placeholder={t('profile.manual-characters.surname')}
              maxLength={12}
              className='w-40'
            />
            <CharacterClassSelect
              value={characterClass}
              setValue={setCharacterClass}
              className='w-48'
            />
            <Button
              type='submit'
              loading={claimMutation.isPending}
              disabled={
                !selectedRealm || !characterName || !surname || !characterClass
              }
            >
              {t('profile.manual-characters.add')}
            </Button>
          </div>
        </form>
      </Card>

      <CharacterTable
        actionable={false}
        manual={true}
        characterReferences={characters}
      />
    </Section>
  )
}

export default ManualCharacters
